/**
 * Map the visitor's IP-derived country to the HubSpot "countries" option value
 * used in the thank-you form. The lookup and its sessionStorage cache live in
 * src/utils/userGeo.js; this module only owns the HubSpot mapping.
 */
import { allCountries } from 'country-telephone-data';
import countryOptions from '../data/countryOptions.json';
import { getCachedUserCountryCode, getUserCountryCode } from './userGeo';

/**
 * Map ISO2 (e.g. "us", "fr") to HubSpot country option value (e.g. "United States +1").
 * Uses country-telephone-data for name + dialCode and matches against countryOptions.
 */
function mapIso2ToHubSpotValue(iso2) {
  const c = allCountries.find((item) => item.iso2 === (iso2 || '').toLowerCase());
  if (!c || !c.dialCode) return null;
  const suffix = ` +${c.dialCode}`;
  const exact = `${c.name} +${c.dialCode}`;
  const opt = countryOptions.find(
    (o) =>
      o.value === exact ||
      (o.value.endsWith(suffix) && o.value.startsWith(c.name))
  );
  return opt ? opt.value : null;
}

/**
 * Synchronous read of cached ISO2 (sessionStorage). Used to resolve UK landing
 * before first paint when the user already hit geo on another page this session.
 */
export function getCachedVisitorCountryIso2() {
  return getCachedUserCountryCode();
}

/**
 * Returns the user's country ISO2 from the session cache, or by looking it up.
 */
export async function getVisitorCountryIso2() {
  return getUserCountryCode();
}

/**
 * Returns the HubSpot-style country value to use as default for the location dropdown,
 * or null if it can't be determined or doesn't match our options.
 */
export async function getDefaultLocationValue() {
  const iso2 = await getVisitorCountryIso2();
  return iso2 ? mapIso2ToHubSpotValue(iso2) : null;
}
